import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '@web-env/environment';

import { FileManagerHttpService } from '@web/core/services/REST/file-manager.http.service';
import { UserFacade } from '@web/store/user/user.facade';

import { buildUploadedImageUrl, isSupportedImageFile } from './screenshot-upload.util';

/** Where a chat-pasted screenshot lands — same top-level folder every AI-imported image already uses. */
const UPLOAD_FOLDER = '/';

/**
 * Longest edge a screenshot is downscaled to before upload — matches Anthropic's own documented
 * vision input cap (an image bigger than this is downsized server-side before the model ever sees
 * it anyway), so sending more is pure waste. It is also what keeps the request small enough to send
 * at all: `SendpulseChatTransport` runs `generateText` with a plain `image_url` pointing at this
 * upload's CDN url, but `createOpenAICompatible()` never declares `supportedUrls`, so the AI SDK
 * treats EVERY url as unfetchable by the provider and downloads + re-embeds it as base64 before the
 * request ever leaves the browser — a full-resolution retina screenshot (5-10 MB) becomes an even
 * bigger base64 string and `stage.sendpulse.test` returns 413 (observed live 2026-09-09: a 6.7 MB
 * payload). Resizing here fixes that regardless of whether `supportedUrls` is ever configured,
 * rather than betting on ai-router-service being able to fetch our CDN url itself.
 */
const MAX_UPLOAD_DIMENSION = 1568;
const RESIZED_JPEG_QUALITY = 0.85;

/** GIFs would lose their animation if flattened onto a canvas — left at whatever size the browser produced them. */
const RESIZABLE_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/webp']);

/**
 * Best-effort — returns the ORIGINAL file on anything that goes wrong (decode failure, no 2d
 * context, an unsupported format) rather than blocking the upload over a resize that is a nice-to-
 * have, not a hard requirement. Also returns the original unchanged when it is already small enough:
 * re-encoding a small PNG as JPEG can grow it, not shrink it.
 */
async function downscaleForUpload(file: File): Promise<File> {
    if (!RESIZABLE_TYPES.has(file.type.toLowerCase())) {
        return file;
    }

    try {
        const bitmap = await createImageBitmap(file);

        try {
            const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height));

            if (scale === 1) {
                return file;
            }

            const canvas = document.createElement('canvas');
            canvas.width = Math.round(bitmap.width * scale);
            canvas.height = Math.round(bitmap.height * scale);

            const context = canvas.getContext('2d');

            if (!context) {
                return file;
            }

            context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

            const blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, 'image/jpeg', RESIZED_JPEG_QUALITY)
            );

            if (!blob) {
                return file;
            }

            return new File([blob], `${file.name.replace(/\.[a-z0-9]+$/i, '')}.jpg`, { type: 'image/jpeg' });
        } finally {
            bitmap.close();
        }
    } catch {
        return file;
    }
}

/**
 * `message` is a TRANSLATION KEY, not display text — mirrors how `errorMessage()` on the component
 * already renders pre-translated text for a chat failure, except here the component itself owns the
 * `| translate` pipe rather than the error carrying finished text, since this error can be thrown
 * from a plain `catch` with no `TranslateService` in scope.
 */
export class ScreenshotUploadError extends Error {}

/**
 * Uploads a screenshot pasted or attached in the AI chat drawer to the SAME file manager a human
 * uploading an image widget uses — `FileManagerHttpService.loadFile()`, already wired for auth via
 * `JwtInterceptor` and already the thing `SiteEffects.uploadImage$` calls.
 *
 * This deliberately does NOT go through `apps/mcp-backend`'s own `/images/upload` route (built for
 * this same feature, then found to be unreachable): mcp-backend has no Traefik route and no CORS —
 * it is called only server-to-server, by `mcp-server`, with the caller's own Authorization header as
 * its only auth. A public route in front of it would be an open proxy onto every user's pages. The
 * browser already has a working upload path; this service reuses it rather than routing around it.
 *
 * Deliberately skips `preparedImageData`/`extendWebFileDataPromise` (`common/helpers/file.ts`): those
 * build a full widget-ready `SelectedFileExtended` — srcSets, presentation defaults, an `Image()`
 * load round-trip to read width/height. None of that is needed here, only the public url to insert
 * into the chat message as `[image:<url>]` — `mcp-backend`'s `describeStoredFile` recognizes that
 * exact url shape and treats it as already stored, so the widget tool that receives it does not
 * re-download and re-upload a second copy.
 */
@Injectable({ providedIn: 'root' })
export class ScreenshotUploadService {
    private readonly fileManagerHttpService = inject(FileManagerHttpService);
    private readonly userFacade = inject(UserFacade);

    public async upload(file: File): Promise<string> {
        if (!isSupportedImageFile(file)) {
            throw new ScreenshotUploadError('ai_chat_attach_image_error_type');
        }

        const cdnInfo = await firstValueFrom(this.userFacade.userCdnInfo$);

        if (!cdnInfo?.userHash) {
            throw new ScreenshotUploadError('ai_chat_attach_image_error_generic');
        }

        const uploadFile = await downscaleForUpload(file);

        // The upload response is a bare array of uploaded files (`content[]`, plural) — not the
        // `{data: ...}` envelope `loadFile`'s own return type suggests. Mirrors how
        // `SiteEffects.uploadImage$` reads it (`images[0]`), the one other caller of this method.
        const response: unknown = await firstValueFrom(
            this.fileManagerHttpService.loadFile({ file: uploadFile, folder: UPLOAD_FOLDER })
        );
        const uploaded = Array.isArray(response) ? (response[0] as { name?: string } | undefined) : undefined;

        if (!uploaded?.name) {
            throw new ScreenshotUploadError('ai_chat_attach_image_error_generic');
        }

        return buildUploadedImageUrl(environment.fileManagerCdnUrl, cdnInfo.userHash, UPLOAD_FOLDER, uploaded.name);
    }
}
